import React, { useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Card, CardHeader, CardContent } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useMeContext } from '~/modules/me/useMeContext';
import { NoDataItem } from '~/modules/common/components/NoDataItem';
import { CardLoading } from '~/modules/common/components/DetailsPage/Card';
import { CertificateIcon as LicensesCertificatesIcon } from '~/modules/common/components/Icons';
import { useCertificates } from '~/modules/resource-management/resourceUserDetails/hooks';
import { RedirectToUnhandledError } from '~/modules/common/unhandled-error';
import { getUserPermittedActions } from '../util';
import CertificatesCardTitle from './CertificatesCardTitle';
import EditCertificate from './EditCertificate';
import AddCertificate from './AddCertificate';
import { mapCertificateKey } from './hooks/combineCertificates';
import { useCertificateNudgeActions } from './hooks/useCertificateNudgeActions';

const useStyles = makeStyles(theme => ({
  avatar: {
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center'
  },
  title: {
    ...theme.typography.h6,
    lineHeight: `${theme.spacing(3)}px`,
    minHeight: theme.spacing(3),
    wordBreak: 'break-word',
    wordWrap: 'break-word',
    hyphens: 'auto',
    color: theme.palette.text.secondary
  },
  headerRoot: {
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  contentRoot: {
    paddingTop: '0px',
    '&:before': {
      content: '""',
      display: 'block',
      margin: '0 auto 24px auto',
      width: '100%'
    }
  }
}));

const avatar = <LicensesCertificatesIcon />;
const title = (
  <FormattedMessage id="resourceCertificatesAndLicensesCard.title" />
);

export const Certificates = ({
  editable = false,
  userDetails,
  isResourceDetailsLoading,
  ariaAttributes = {}
}) => {
  const {
    featureFlags: {
      isPsaPrpWcagFixesQ4Enabled,
      isPsaPraaResourceMatchingEnabled
    }
  } = useMeContext();
  const classes = useStyles();
  const cardHeaderClasses = useMemo(
    () => ({
      title: classes.title,
      avatar: classes.avatar,
      root: classes.headerRoot
    }),
    [classes.avatar, classes.headerRoot, classes.title]
  );

  const cardContentClasses = useMemo(() => ({ root: classes.contentRoot }), [
    classes.contentRoot
  ]);

  const { slug, uri } = userDetails || {};

  const {
    error: errorInLoadingCertificates,
    certificates = [],
    isCertificateLoading
  } = useCertificates({
    userSlug: slug
  });

  const permittedActions = getUserPermittedActions(userDetails);
  const canEditSkills = permittedActions.includes(
    'urn:replicon:user-action:edit-skill-assignments'
  );

  const {
    isUserProfileCertificatesLoading,
    combinedCertificates,
    onAcceptCertificateNudges,
    onRejectCertificateNudges,
    acceptAllCertificateNudges,
    rejectAllCertificateNudges
  } = useCertificateNudgeActions({
    userUri: uri,
    userSlug: slug,
    certificates,
    editable,
    canCreateSkills: canEditSkills,
    isPsaPraaResourceMatchingEnabled
  });

  const titleWithActions = useMemo(() => {
    if (!canEditSkills || !isPsaPraaResourceMatchingEnabled) return title;

    return (
      <CertificatesCardTitle
        editable={editable}
        acceptAllCertificateNudges={acceptAllCertificateNudges}
        rejectAllCertificateNudges={rejectAllCertificateNudges}
        canEditSkills={canEditSkills}
      />
    );
  }, [
    editable,
    canEditSkills,
    acceptAllCertificateNudges,
    rejectAllCertificateNudges,
    isPsaPraaResourceMatchingEnabled
  ]);

  const visibleCertificates =
    isPsaPraaResourceMatchingEnabled && editable
      ? combinedCertificates
      : certificates;

  const { role = 'heading', 'aria-level': ariaLevel = 2 } = ariaAttributes;

  if (errorInLoadingCertificates) return <RedirectToUnhandledError />;

  if (
    isCertificateLoading ||
    isResourceDetailsLoading ||
    (isPsaPraaResourceMatchingEnabled && isUserProfileCertificatesLoading)
  )
    return <CardLoading avatar={avatar} title={title} />;

  return (
    <Card>
      <CardHeader
        classes={cardHeaderClasses}
        avatar={avatar}
        title={titleWithActions}
        {...(isPsaPrpWcagFixesQ4Enabled && {
          role,
          'aria-level': ariaLevel
        })}
      />
      <CardContent
        data-qe-id="CertificateContentSection"
        classes={cardContentClasses}
      >
        {visibleCertificates.length > 0 ? (
          visibleCertificates.map(certificate => (
            <EditCertificate
              key={
                certificate.uri ||
                (isPsaPraaResourceMatchingEnabled &&
                  mapCertificateKey(certificate))
              }
              certificate={certificate}
              editable={editable}
              userUri={uri}
              canEditSkills={canEditSkills}
              onAcceptCertificateNudges={onAcceptCertificateNudges}
              onRejectCertificateNudges={onRejectCertificateNudges}
            />
          ))
        ) : (
          <NoDataItem>
            <FormattedMessage id="resourceCertificatesAndLicensesCard.noCertificates" />
          </NoDataItem>
        )}
        {editable && <AddCertificate userUri={uri} />}
      </CardContent>
    </Card>
  );
};

Certificates.propTypes = {
  editable: PropTypes.bool,
  userDetails: PropTypes.object,
  isResourceDetailsLoading: PropTypes.bool,
  ariaAttributes: PropTypes.object
};

export default Certificates;
